/**
 * How an enumeration value is written for a human being.
 *
 * This file exists because a database string has a way of reaching a screen, and once it does it
 * is one copy and paste from reaching a client. Every label here is checked against
 * `../faithfully-kept-business/docs/what-we-cannot-say.md`: nothing claims a credential the
 * business does not hold, and the words `employee`, `staff` and `team member` appear nowhere, in
 * any casing, which a house-style test asserts over this whole repository.
 *
 * Anything without a label here falls back to the raw value with underscores turned into spaces,
 * which is deliberately plain rather than clever: a missing label should look unfinished.
 */
import type {
  AgreementState,
  AssignmentStatus,
  AuthorizationRevokeReason,
  Cadence,
  CallOutcome,
  CaptureMethod,
  ChargeDecision,
  ClosedDateKind,
  DoNotContactReason,
  EmailKind,
  EndedReason,
  EntrySource,
  FailureCode,
  FeedbackFace,
  HeldBackReason,
  HomePlanSource,
  HomeRequestCancelReason,
  HomeRequestKind,
  HomeRequestState,
  InboundMessageState,
  InvoiceState,
  IssueKind,
  IssueState,
  MembershipRevokeReason,
  MembershipState,
  MessageIntentState,
  NoticeMode,
  OfficePreferenceHow,
  PaymentKind,
  PaymentMethod,
  PreferenceCategory,
  PriceSource,
  Role,
  SendTime,
  ServiceLine,
  SilenceReason,
  SuppressionScope,
  TaskState,
  TextingRegrantSkip,
  TimelineKind,
  VisitChangeKind,
  VisitKind,
  VisitOrigin,
  VisitStatus,
} from './enums.ts';

export const ROLE_LABELS: Record<Role, string> = Object.freeze({
  owner: 'Owner',
  manager: 'Manager',
  office: 'Office',
  cleaner: 'Cleaner',
});

/** The cadence words the published card uses. It sells "every 4 weeks" and never "monthly". */
export const CADENCE_LABELS: Record<Cadence, string> = Object.freeze({
  one_time: 'One time',
  every_4_weeks: 'Every 4 weeks',
  every_2_weeks: 'Every 2 weeks',
  weekly: 'Weekly',
});

/**
 * What held a quote back, as the office reads it (ops #86): the business's own labels, 2026-10-22,
 * `scope` reading "What is included".
 */
export const HELD_BACK_LABELS: Record<HeldBackReason, string> = Object.freeze({
  price: 'Price',
  timing: 'Timing',
  thinking_it_over: 'Thinking it over',
  comparing_quotes: 'Comparing quotes',
  scope: 'What is included',
  no_answer: 'No answer',
  other: 'Other',
});

export const SERVICE_LINE_LABELS: Record<ServiceLine, string> = Object.freeze({
  house_cleaning: 'House cleaning',
  home_watch: 'Home watch',
  turnover: 'Turnover cleaning',
});

export const VISIT_KIND_LABELS: Record<VisitKind, string> = Object.freeze({
  recurring: 'Recurring clean',
  one_time: 'One-off clean',
  deep_clean: 'Deep clean',
  move_out: 'Move-out or move-in',
  home_watch: 'Home watch',
  turnover: 'Turnover',
});

export const VISIT_STATUS_LABELS: Record<VisitStatus, string> = Object.freeze({
  planned: 'Planned',
  confirmed: 'Confirmed',
  in_progress: 'In progress',
  done: 'Done',
  cancelled: 'Cancelled',
  no_access: 'Could not get in',
});

/** Where a visit row came from. Shown small, because the date is what matters. */
export const VISIT_ORIGIN_LABELS: Record<VisitOrigin, string> = Object.freeze({
  materialised: 'From the standing schedule',
  manual: 'Booked by hand',
  moved: 'Moved',
  access_retry: 'Return after a locked door',
  reclean: 'Re-clean',
});

export const AGREEMENT_STATE_LABELS: Record<AgreementState, string> = Object.freeze({
  draft: 'Draft',
  active: 'Active',
  paused: 'Paused',
  ended: 'Ended',
});

/** Why an agreement ended. The first one is not churn, and the breach list must never count it as such. */
export const ENDED_REASON_LABELS: Record<EndedReason, string> = Object.freeze({
  superseded_cadence_change: 'Replaced by a new cadence',
  client_ended: 'The client ended it',
  we_ended: 'We ended it',
  moved_away: 'The client moved away',
  season_ended: 'The season ended',
  non_payment: 'Not paid',
  never_started: 'Never started',
});

/** What kind of change was made to a visit. Read on the visit, and on the breach list. */
export const VISIT_CHANGE_KIND_LABELS: Record<VisitChangeKind, string> = Object.freeze({
  moved: 'Moved to another day',
  cancelled_by_client: 'Cancelled by the client',
  cancelled_by_us: 'Cancelled by us',
  paused: 'Paused',
  no_access_rescheduled: 'Could not get in, rebooked',
  window_changed: 'Arrival window changed',
  cleaner_changed: 'A different cleaner',
});

/**
 * Why no message was sent about a change. Each is a real reason the client already knows, or a
 * real reason she could not be told, and the person choosing it is saying so on the record.
 */
export const SILENCE_REASON_LABELS: Record<SilenceReason, string> = Object.freeze({
  told_by_phone: 'I told them by phone',
  told_in_person: 'I told them in person',
  client_requested_the_change: 'They asked for this change themselves',
  no_contact_details_yet: 'There is no way to reach them yet',
  system_correction: 'Correcting a mistake in the record, nothing changed for them',
});

export const CHARGE_DECISION_LABELS: Record<ChargeDecision, string> = Object.freeze({
  not_decided: 'Not decided yet',
  no_charge: 'No charge',
  half_rate: 'Half the visit rate',
  full_rate: 'The full visit rate',
});

/** Where a home plan's words came from (ops #74 item 6), as the home's page names it. */
export const HOME_PLAN_SOURCE_LABELS: Record<HomePlanSource, string> = Object.freeze({
  client_via_home: 'Sent by the household through home',
  office_from_call: 'Taken down on a call',
  office_from_walkthrough: 'Taken down at a walkthrough',
});

export const ASSIGNMENT_STATUS_LABELS: Record<AssignmentStatus, string> = Object.freeze({
  assigned: 'Assigned',
  accepted: 'Accepted',
  declined: 'Declined',
});

/** The per-client default for the morning arrival text. */
export const NOTICE_MODE_LABELS: Record<NoticeMode, string> = Object.freeze({
  automatic: 'Send it automatically',
  ask_first: 'Show it to me first',
  never: 'Never send one',
});

/**
 * Why a message did not go, for the person who has to put it right. The phone system's own sentence
 * travels beside the code and is written for a person too; this is the part that stays the same
 * whichever words it chose. The first eleven are a message the phone system took and could not
 * deliver, the last nine an instruction it refused, and a screen says which.
 */
export const FAILURE_CODE_LABELS: Record<FailureCode, string> = Object.freeze({
  not_enabled: 'Texting is not switched on yet',
  recipient_stopped: 'They replied STOP, so no text may go to their number',
  number_held: 'The phone system is holding texts to this number until it is released',
  first_text_needs_opt_out:
    'A first text to a number must say how to opt out, and this one did not',
  daily_cap: 'This number has had as many texts today as one day allows',
  expired: 'Its time ran out before it could go, so it was not sent late',
  carrier_filtered: 'The carrier filtered it out',
  undeliverable: 'The carrier could not deliver it to this number',
  invalid_number: 'This number cannot take a text',
  unconfirmed: 'The carrier never confirmed it arrived, so they may not have it',
  cancelled: 'It was withdrawn before it went',
  bad_json: 'The phone system could not read the instruction',
  key_reused: 'The phone system already holds a different message under the same key',
  invalid: 'The phone system could not accept one of its fields',
  unknown_instruction: 'The phone system does not know this instruction',
  kind_not_filed: 'This kind of text is not one the texting registration covers',
  basis_missing: 'The text named no consent it was sent under',
  no_business_name: 'The text does not say it is from Faithfully Kept',
  too_long: 'The text is longer than three text segments',
  link_not_allowed: 'The text carries a link the phone system does not allow',
});

/**
 * HOW A CONSENT WAS TAKEN, which is the evidence rather than a detail. These are written to finish
 * the sentence "recorded on 8 September, ...", so each one reads as something that happened rather
 * than as the name of a column, and none of them claims a document the business does not hold.
 *
 * Here rather than beside the screens since ops #51, because both halves say it: the household page
 * says how a position was recorded, and the register's refusal of a text names how a consent that
 * does not count for texts was given (`schedule/consent.ts`), in these words in lower case.
 * `booking_form` is the box on the website quote form, the one way anybody opts in to texts.
 */
export const CAPTURE_METHOD_LABELS: Record<CaptureMethod, string> = Object.freeze({
  booking_form: 'Ticked on the website form',
  verbal_on_call: 'Said on a call',
  email_reply: 'Said in an email',
  sms_reply: 'Said in a text',
  in_person: 'Said in person',
  written: 'In writing',
});

/**
 * WHY THE RE-GRANT FROM THE WEBSITE TICK PASSED SOMEBODY BY (ops #55, item 5), as the owner reads it
 * on Settings beside each count. Each says what stands in the way, and none names a person.
 */
export const TEXTING_REGRANT_SKIP_LABELS: Record<TextingRegrantSkip, string> = Object.freeze({
  household_removed: 'The household was removed',
  household_ended: 'The household has ended',
  wording_not_filed: 'Ticked under words the carrier did not approve',
  page_not_counted: 'Ticked on a page whose box does not count',
  number_unknown: 'The tick names no number',
  number_changed: 'Nobody on the household holds the ticked number now',
  forgotten: 'Asked to be forgotten',
  contact_removed: 'Removed from the household',
  already_granted: 'Already has texting consent',
  withdrawn_later: 'Withdrew consent to texts at or after the tick',
  unticked_later: 'Left the box unticked on a later form',
  carrier_stop: 'Sent STOP',
  do_not_contact: 'Marked do not contact',
  suppressed: 'Asked not to be texted',
});

export const CLOSED_DATE_KIND_LABELS: Record<ClosedDateKind, string> = Object.freeze({
  holiday: 'Holiday',
  owner_unavailable: 'Nobody available',
  storm: 'Storm',
  other: 'Other',
});

/**
 * Read on the breach list, so each one says why a figure differs from the card rather than merely
 * naming a constant. `late_cancellation_half_rate` names its own upstream defect on purpose: see
 * `erd.md` Part 9. Nobody should have to go and look it up to know why that row is there.
 */
export const PRICE_SOURCE_LABELS: Record<PriceSource, string> = Object.freeze({
  card: 'Published card',
  card_deep_clean: 'Published deep clean rate',
  card_move_out: 'Published move-out rate',
  card_addon: 'Published add-on rate',
  quoted_walkthrough: 'Quoted after a walkthrough, as the card says larger homes are',
  sanctioned_zero: 'No charge, agreed and recorded',
  reclean_no_charge: 'Re-clean at no charge, as published',
  missed_visit_no_charge: 'Missed by us, so free when we do it, as published',
  late_cancellation_half_rate:
    'Half the visit rate for a late cancellation. Published on the policy page and not on the rate card, which is a contradiction to fix upstream',
});

export function humanise(value: string): string {
  return value.replace(/[_-]/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/* ------------------------------------------------------------------ *
 * Read by both halves since the household timeline (ops #84): the screens draw them as
 * pills, and the timeline read writes its rows in the same words on the server.
 * ------------------------------------------------------------------ */

export const ISSUE_KIND_LABELS: Record<IssueKind, string> = Object.freeze({
  damage: 'Damage',
  breakage: 'Something broke',
  missed_area: 'An area was missed',
  complaint: 'A complaint',
  access_problem: 'Could not get in',
  safety: 'Safety',
  other: 'Something else',
});

export const ISSUE_STATE_LABELS: Record<IssueState, string> = Object.freeze({
  open: 'Open',
  acknowledged: 'Acknowledged',
  remedied: 'Put right',
  closed: 'Closed',
});

export const INVOICE_STATE_LABELS: Record<InvoiceState, string> = Object.freeze({
  draft: 'Draft',
  sent: 'Sent',
  part_paid: 'Part paid',
  paid: 'Paid',
  overdue: 'Overdue',
  void: 'Void',
  written_off: 'Written off',
});

export const PAYMENT_KIND_LABELS: Record<PaymentKind, string> = Object.freeze({
  payment: 'Payment',
  refund: 'Refund',
  correction: 'Correction',
});

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = Object.freeze({
  card: 'Card',
  bank_transfer: 'Bank transfer',
  zelle: 'Zelle',
  check: 'Check',
  cash: 'Cash',
  other: 'Other',
});

export const ENTRY_SOURCE_LABELS: Record<EntrySource, string> = Object.freeze({
  square: 'Reported by Square',
  owner_entered: 'Typed in by an owner',
  bank_statement: 'Read off the bank statement',
  stripe: 'Reported by Stripe',
});

/**
 * WHERE A FOLLOW-UP IS. Two of these four are written by nothing at all: a task is open or it is
 * done, and the door refuses any other word. `doing` and `cancelled` are in the vocabulary because
 * the CHECK constraint carries them, and they are named here so a row that somehow holds one is
 * still drawn in words rather than as a bare database string.
 */
export const TASK_STATE_LABELS: Record<TaskState, string> = Object.freeze({
  open: 'To do',
  doing: 'Being done',
  done: 'Done',
  cancelled: 'Cancelled',
});

/**
 * How a call finished, as the office would say it out loud. The phone system's five outcomes,
 * and the three that mean nobody spoke to her are written so that reading the list is enough to
 * see which one needs a call back.
 */
export const CALL_OUTCOME_LABELS: Record<CallOutcome, string> = Object.freeze({
  answered: 'Answered',
  voicemail: 'Left a voicemail',
  missed: 'Missed',
  abandoned: 'Hung up before we answered',
  blocked: 'Blocked',
});

export const INBOUND_MESSAGE_STATE_LABELS: Record<InboundMessageState, string> = Object.freeze({
  open: 'Not handled yet',
  handled: 'Handled',
  spam: 'Spam',
});

/**
 * WHERE A TEXT OPS COMPOSED STANDS, as a pill beside it in a household's thread (ops #26, part 2).
 * The first two are ops's own, the third is the phone system holding it, and the rest are how it
 * ended. Why one did not go is said beside it, in the phone system's word turned into a sentence.
 */
export const MESSAGE_INTENT_STATE_LABELS: Record<MessageIntentState, string> = Object.freeze({
  composed: 'Written, not approved yet',
  approved: 'Approved, waiting to go',
  handed_off: 'With the phone system',
  delivered: 'Delivered',
  failed: 'Did not go',
  cancelled: 'Withdrawn',
  refused_by_consent: 'Not sent, no consent',
});

/**
 * WHAT EACH CLIENT EMAIL WAS (ops #76 O6), as the office names it on the invoice screen's emails, the
 * household's Emails card and a call about one. Office words: the client reads the subject the owner
 * approved, never these.
 */
export const EMAIL_KIND_LABELS: Record<EmailKind, string> = Object.freeze({
  invoice_sent: 'Invoice',
  payment_receipt: 'Receipt',
  invoice_cancelled: 'Invoice cancelled',
  refund_notice: 'Refund',
  payment_problem: 'Payment did not go through',
  invoice_overdue: 'Past due',
  visit_reminder: 'Reminder before a visit',
  schedule_change: 'Change to a visit',
  reply: 'From the office',
  booking_confirmation: 'Booking confirmed',
  visit_finished: 'Visit finished',
  access_problem: 'Getting in',
});

/**
 * THE FOUR FACES OF "HOW DID WE DO?" (ops #46, step 2), as the client taps them on her invoice's page
 * and the office reads them back on the invoice screen. The same four words on both, so what she
 * tapped and what the office sees she tapped are one word.
 */
export const FEEDBACK_FACE_LABELS: Record<FeedbackFace, string> = Object.freeze({
  delighted: 'Delighted',
  happy: 'Happy',
  so_so: 'So-so',
  unhappy: 'Unhappy',
});

/**
 * WHERE ONE PERSON'S DOOR ONTO HOME STANDS (ops #19, step 5), as the pill on her card says it. Home is
 * the household's own page, `home.faithfullykept.com`; ops holds only whether the office said she may
 * see the household there, and whether she has signed in yet.
 */
export const MEMBERSHIP_STATE_LABELS: Record<MembershipState, string> = Object.freeze({
  invited: 'Invited to home',
  granted: 'On home',
  revoked: 'Removed from home',
});

export const MEMBERSHIP_REVOKE_REASON_LABELS: Record<MembershipRevokeReason, string> =
  Object.freeze({
    contact_removed: 'They are no longer on the household',
    household_ended: 'The household ended',
    forgotten: 'They asked to be forgotten',
    office_decision: 'The office removed them',
    superseded: 'A fresh invitation replaced it',
    invitation_expired: 'The invitation expired',
  });

/**
 * WHY A SAVED METHOD WAS STOPPED (ops #46, step 3b), as the office chooses it on the household and
 * reads it back. Every reason the code knows, the forget's among them, though only the forget writes
 * that one and the office's Stop never offers it (`OFFICE_REVOKE_REASONS`).
 */
export const REVOKE_REASON_LABELS: Record<AuthorizationRevokeReason, string> = Object.freeze({
  client_request: 'They asked to stop',
  card_replaced: 'It is being replaced with another card or account',
  method_gone: 'The card or account is gone',
  charge_declined: 'A charge to it was declined',
  forgotten: 'They asked to be forgotten',
  owner_decision: 'The owner decided to stop it',
});

/**
 * WHAT SHE ASKED FOR THROUGH HOME (ops #19, step 7), as the heading of the ask the office opens. Every
 * kind home can send, the ones applied on receipt and the two answered at once among them, though only
 * the ones the office decides are ever shown to it. The thirteen of ops #76 (migration `0037`) and her
 * taking back of her Zelle report (ops #71, migration `0040`) are named here before the door takes
 * them, so the screen never meets a kind it cannot name.
 */
export const HOME_REQUEST_KIND_LABELS: Record<HomeRequestKind, string> = Object.freeze({
  'membership.accepted': 'Signed in at home',
  'visit.skip': 'Skip a clean',
  'visit.move': 'Move a clean',
  'visit.addon': 'Something extra on a clean',
  'visit.instruction': 'Something for the cleaner',
  'visit.feedback': 'How the clean went',
  'invoice.feedback': 'How the clean went',
  'invoice.pay': 'A page to pay through',
  'invoice.pay_by_zelle': 'Paying by Zelle',
  'billing.autopay_stop': 'Stop charging automatically',
  'home.edit': 'Change the notes on the home',
  'person.add': 'Add a person',
  message: 'A message',
  'journal.photos': 'The photos of a clean',
  'invoice.document': 'A PDF of an invoice or a receipt',
  'invoice.zelle_sent': 'Sent it by Zelle',
  'billing.autopay_start': 'Save a way to pay automatically',
  'schedule.pause': 'Pause the regular cleans',
  'schedule.resume': 'Start the regular cleans again',
  'schedule.change': 'Change the regular cleans',
  'service.end': 'End a service',
  'person.contact_change': 'Change their own contact details',
  'person.remove': 'Take a person off the household',
  'person.change': 'Change the homes a person sees',
  'person.leave': 'Take them off home',
  'preferences.set': 'Change what they hear from us',
  'texts.stop': 'Stop texts',
  'texts.start': 'Start texts again',
  'preferences.time_zone': 'Change their time zone',
  'invoice.zelle_withdrawn': 'Did not send the Zelle after all',
});

export const HOME_REQUEST_STATE_LABELS: Record<HomeRequestState, string> = Object.freeze({
  received: 'Waiting on the office',
  accepted: 'Accepted',
  declined: 'Declined',
  cancelled: 'Cancelled',
});

export const HOME_REQUEST_CANCEL_REASON_LABELS: Record<HomeRequestCancelReason, string> =
  Object.freeze({
    membership_revoked: 'They are no longer on home',
    household_ended: 'The household ended',
    visit_no_longer_open: 'The clean was no longer open to a change',
    withdrawn: 'They withdrew it',
    schedule_ended: 'The schedule ended, or the home was removed, first',
  });

/**
 * WHY A DO-NOT-CONTACT MARK WAS SET OR LIFTED (ops #44), as the line under a person, the reason
 * lists and the history say it. Her words in the note stay hers; these are the office's.
 */
export const DO_NOT_CONTACT_REASON_LABELS: Record<DoNotContactReason, string> = Object.freeze({
  client_request: 'They asked us',
  forget_request: 'They asked to be forgotten',
  carrier_stop: 'They texted STOP',
  bounce: 'It no longer reaches them',
  wrong_number: 'It is not theirs',
  owner_decision: 'The business decided',
  came_back: 'They came back and asked to hear from us',
  recorded_in_error: 'It should never have been set',
});

export const MARK_SCOPE_LABELS: Record<SuppressionScope, string> = Object.freeze({
  all: 'Every way: texts, emails and calls',
  sms: 'Texts',
  email: 'Emails',
  call: 'Calls',
});

/**
 * THE TIMELINE'S FILTER CHIPS (ops #84, `docs/plan-client-workplace.md` section 3.1), one a kind of
 * table 3.2, in the words a person picking what to look at would use.
 */
export const TIMELINE_KIND_LABELS: Record<TimelineKind, string> = Object.freeze({
  visit: 'Visits',
  change: 'Changes to visits',
  call_off: 'Cleaners who could not come',
  text_in: 'Texts in',
  text_out: 'Texts out',
  email_out: 'Emails',
  call: 'Calls',
  ask: 'Asks from home',
  invoice: 'Invoices',
  payment: 'Payments',
  feedback: 'Feedback',
  consent: 'Consent',
  mark: 'Do not contact',
  door: 'Door onto home',
  autopay: 'Automatic payment',
  issue: 'Issues',
  task: 'Follow-ups',
  office: 'Office changes',
  preference: 'Message choices',
});

/**
 * WHAT EACH CHOICE ROW IS ABOUT, as the office's card names it (ops #76 phase 2, phase 4 of
 * `docs/plan-preferences.md`): one a category a person may switch, in the words their page at home
 * uses for it.
 */
export const PREFERENCE_CATEGORY_LABELS: Record<PreferenceCategory, string> = Object.freeze({
  visit_changes: 'Changes to a visit',
  visit_reminders: 'Reminders before a visit',
  visit_day: 'News on the day of a visit',
  home_checks: 'Home check reports',
  invoices: 'Invoices',
  receipts: 'Receipts',
  reviews: 'Asks for their feedback',
});

/** The part of the day a reminder goes at, as the card says it. */
export const SEND_TIME_LABELS: Record<SendTime, string> = Object.freeze({
  morning: 'In the morning',
  afternoon: 'In the afternoon',
  evening: 'In the evening',
});

/** How a person told the office what they want sent, as the card's form asks it. */
export const OFFICE_PREFERENCE_HOW_LABELS: Record<OfficePreferenceHow, string> = Object.freeze({
  call: 'On a call',
  email: 'By email',
  text: 'By text',
  in_person: 'In person',
});
